import { Injectable, inject, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { SKIP_LOADING } from '../interceptors/loading.interceptor';
import {
  injectQuery,
  injectMutation,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { environment } from '../../environments/environment';
import { apiConfig } from '../utils/api-config.util';
import {
  SimpleInAppNotification,
  SimpleNotificationResponse,
} from '@ics-referral-hub/shared-types';
import { AuthService } from '../auth/services/auth.service';
import { MessageNotificationService } from './message-notification.service';
import { catchError, throwError } from 'rxjs';
import { Logger } from '../shared/utils/logger.util';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private queryClient = inject(QueryClient);
  private router = inject(Router);
  private messageNotificationService = inject(MessageNotificationService);
  private readonly apiPrefix = apiConfig.prefix;
  private baseUrl = `${environment.apiUrl}${this.apiPrefix}/in-app-notifications`;

  // Query to get user notifications
  getNotificationsQuery = injectQuery(() => ({
    queryKey: ['notifications'],
    queryFn: (): Promise<{
      success: boolean;
      data: SimpleNotificationResponse;
    }> => {
      // Verify authentication before making the request
      if (!this.authService.isAuthenticated()) {
        return Promise.reject(new Error('User is not authenticated'));
      }

      return this.http
        .get<{
          success: boolean;
          data: SimpleNotificationResponse;
        }>(this.baseUrl, {
          context: new HttpContext().set(SKIP_LOADING, true)
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            Logger.error('Failed to fetch notifications:', error);
            
            // Handle 401 errors specifically
            if (error.status === 401) {
              Logger.error('Authentication failed for notifications request');
            }
            
            return throwError(() => error);
          })
        )
        .toPromise() as Promise<{
        success: boolean;
        data: SimpleNotificationResponse;
      }>;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    refetchOnWindowFocus: true, // Keep notifications fresh when user returns (uses SKIP_LOADING)
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
  }));

  // Mutation to mark notifications as read
  markAsReadMutation = injectMutation(() => ({
    mutationFn: () => {
      // Verify authentication before making the request
      if (!this.authService.isAuthenticated()) {
        return Promise.reject(new Error('User is not authenticated'));
      }

      return this.http
        .post(`${this.baseUrl}/mark-read`, {})
        .pipe(
          catchError((error: HttpErrorResponse) => {
            Logger.error('Failed to mark notifications as read:', error);
            
            // Handle 401 errors specifically
            if (error.status === 401) {
              Logger.error('Authentication failed for mark-read request');
            }
            
            return throwError(() => error);
          })
        )
        .toPromise();
    },
    onSuccess: () => {
      // Invalidate both regular notifications and message notifications so the
      // badge count and panel both reflect the updated read state
      this.queryClient.invalidateQueries({ queryKey: ['notifications'] });
      this.queryClient.invalidateQueries({ queryKey: ['message-notifications'] });
    },
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  }));

  // Combined notifications (regular + derived message-thread bells).
  // Exclude legacy persisted rows with metadata.type === 'message' — those
  // duplicated GET /messages/notifications entries in the bell.
  notifications = computed((): SimpleInAppNotification[] => {
    const regularNotifications = (
      this.getNotificationsQuery.data()?.data?.notifications || []
    ).filter((n) => n.metadata?.['type'] !== 'message');
    const messageNotifications =
      this.messageNotificationService.getNotificationsForBell();

    return [...regularNotifications, ...messageNotifications].sort(
      (a, b) => b.createdAt - a.createdAt,
    );
  });

  // Regular notification new count only (used for mark-as-read logic)
  regularNewCount = computed((): number => {
    const data = this.getNotificationsQuery.data()?.data;
    if (!data) {
      return 0;
    }
    // Prefer API newCount when the list is already filtered server-side;
    // recompute locally so legacy message rows never inflate the badge.
    const lastReadAt = data.lastReadAt || 0;
    return (data.notifications || []).filter(
      (n) =>
        n.metadata?.['type'] !== 'message' && n.createdAt > lastReadAt,
    ).length;
  });

  // Combined new notification count (regular + message notifications)
  newCount = computed((): number => {
    const messageNewCount = this.messageNotificationService.totalUnreadMessages();
    return this.regularNewCount() + messageNewCount;
  });

  // Get last read timestamp
  get lastReadAt(): number {
    return this.getNotificationsQuery.data()?.data?.lastReadAt || 0;
  }

  // Check if query is loading
  get isLoading(): boolean {
    return this.getNotificationsQuery.isLoading();
  }

  // Mark notifications as read
  markAsRead(): void {
    this.markAsReadMutation.mutate();
  }

  // Manual refetch
  refetch(): void {
    this.getNotificationsQuery.refetch();
  }

  // Handle notification click navigation
  handleNotificationClick(notification: SimpleInAppNotification): void {
    // Message notifications are identified by their ID prefix
    if (notification._id?.startsWith('message-')) {
      this.messageNotificationService.handleMessageNotificationClick(notification);
      return;
    }

    // For ANY notification that carries a referralId, invalidate that referral's
    // cached data so the user always sees fresh data when they land on the page.
    // This covers status-change notifications, new-document notifications, etc.
    const referralId = notification.metadata?.['referralId'] as string | undefined;
    if (referralId) {
      this.invalidateReferralCaches(referralId);
      // Always take users to the referral details (profile) when we know the referralId
      this.router.navigate(['/referrals', referralId, 'profile']);
      return;
    }

    // Handle regular notifications
    if (notification.actionUrl) {
      // Parse the action URL and navigate accordingly
      if (notification.actionUrl.startsWith('/')) {
        // Internal route - use Angular router
        const urlParts = notification.actionUrl.split('?');
        const path = urlParts[0];
        const queryString = urlParts[1];
        
        if (queryString) {
          const queryParams = new URLSearchParams(queryString);
          const queryParamsObj: any = {};
          queryParams.forEach((value, key) => {
            queryParamsObj[key] = value;
          });
          this.router.navigate([path], { queryParams: queryParamsObj });
        } else {
          this.router.navigate([path]);
        }
      } else {
        // External URL
        window.location.href = notification.actionUrl;
      }
    }
  }

  /**
   * Invalidate all cached data for a specific referral so the UI
   * shows fresh data when the user navigates to it from a notification.
   */
  private invalidateReferralCaches(referralId: string): void {
    // Referral detail + sub-resources
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'detail', referralId] });
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'completeness', referralId] });
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'documents', referralId] });
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'notes', referralId] });
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'status-history', referralId] });
    // Messages for this referral
    this.queryClient.invalidateQueries({ queryKey: ['messages', 'referral', referralId] });
    // Referral lists & stats (the event that triggered the notification may have changed counts)
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'list'] });
    this.queryClient.invalidateQueries({ queryKey: ['referrals', 'stats'] });
  }

  /**
   * Verify authentication setup for debugging purposes
   * Returns information about the current authentication state
   */
  verifyAuthentication(): {
    isAuthenticated: boolean;
    hasToken: boolean;
    baseUrl: string;
    willUseInterceptor: boolean;
  } {
    const isAuthenticated = this.authService.isAuthenticated();
    const token = this.authService.accessToken();
    const hasToken = !!token;
    const willUseInterceptor = this.baseUrl.startsWith(environment.apiUrl);

    const result = {
      isAuthenticated,
      hasToken,
      baseUrl: this.baseUrl,
      willUseInterceptor,
    };

    Logger.log('NotificationService Authentication Status:', result);
    
    if (!isAuthenticated) {
      Logger.warn('⚠️ User is not authenticated - requests will fail');
    } else if (!hasToken) {
      Logger.warn('⚠️ No access token available - requests may fail');
    } else if (!willUseInterceptor) {
      Logger.warn('⚠️ URL does not match interceptor pattern - authentication headers will not be added');
    } else {
      Logger.log('✅ Authentication is properly configured');
    }

    return result;
  }
}
